import React, { Children, cloneElement, Component } from 'react';

import './drop-down-menu.scss';

import { DropDownMenuAPI } from '@philips-emr/tws-components-api';
import { Placement } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { getSlot, isFunction, seizeAndForwardChildrenEvent } from '@philips-emr/tws-utils';

import { CollapseArrow, Popover } from '..';

const headerAcceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.LAYOUT,
];

const contentAcceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.NAVIGATION,
  ComponentType.LAYOUT,
];

@AwesomeComponent({ type: ComponentType.NAVIGATION, api: DropDownMenuAPI })
class DropDownMenu extends Component {
  state = {
    open: false,
  };

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    matchTargetWidth: bool,
    onBeforeClose: eventListener,
    onClose: eventListener,
    onOpen: eventListener,
    showArrow: bool,
    showChevron: bool,
    uuid: string,
  };

  static defaultProps = {
    matchTargetWidth: true,
    showArrow: true,
    showChevron: true,
  };

  shouldClose = () => {
    const { onBeforeClose } = this.props;
    let close = true;
    if (isFunction(onBeforeClose) && this.state.open) {
      const abortFn = () => close = false;

      onBeforeClose({ abort: abortFn });
    }

    return close;
  };

  togglePopover = () => {
    const { open } = this.state;
    const { onClose, onOpen } = this.props;

    let triggerEvent;

    if (open) {
      if (!this.shouldClose()) {
        return;
      }

      triggerEvent = () => isFunction(onClose) && onClose();
    } else {
      triggerEvent = () => isFunction(onOpen) && onOpen();
    }

    this.setState(({ open }) => ({ open: !open }), triggerEvent);
  };

  onItemClick = () => {
    this.popoverToggle();
  };

  renderHeader() {
    const { open } = this.state;
    const { children, uuid, showChevron } = this.props;

    const childrenArray = Children.toArray(children);
    const header = getSlot(childrenArray, 'header');

    const Container = ContainerProvider.provide(this);

    return (
      <div className="c-drop-down-menu__header" onClick={this.togglePopover}>
        <Container
          acceptedTypes={headerAcceptedTypes}
          className="u-expand"
          slot="header"
          uuid={uuid}
        >
          {Children.map(header, (child) => cloneElement(child))}
        </Container>
        {showChevron &&  <CollapseArrow collapsed={!open} />}
      </div>
    );
  }

  renderContent() {
    const { children, uuid } = this.props;

    const childrenArray = Children.toArray(children);
    const content = getSlot(childrenArray, 'content');

    const Container = ContainerProvider.provide(this);

    return (
      <Container
        acceptedTypes={contentAcceptedTypes}
        className="c-drop-down-menu__container u-expand"
        slot="content"
        uuid={uuid}
      >
        {seizeAndForwardChildrenEvent(content, 'onClick', this.onItemClick)}
      </Container>
    );
  }

  render() {
    const { matchTargetWidth, showArrow, showChevron } = this.props;
    const { open } = this.state;

    return (
      <div className="c-drop-down-menu">
        <Popover
          placement={Placement.BOTTOM}
          onClose={this.togglePopover}
          open={open}
          showArrow={showArrow && !showChevron}
          matchTargetWidth={matchTargetWidth}
        >
          {this.renderHeader()}
          <Popover.Content>
            {this.renderContent()}
          </Popover.Content>
        </Popover>
      </div>
    );
  }
}

export default DropDownMenu;
